import type { DiagnosticProfile } from '../diagnostics/journal';
import type { BotStrategyProvenance, ReplayMatchMetadata } from '../replay/replay';
import type { BattleRules, CardId, ContentSnapshot, PlayerId } from './types';

export const ENGINE_VERSION = '1.8.0-network.3';
export const MAX_COMMAND_LEAD_TICKS = 9;

export const DEFAULT_RULES: BattleRules = {
  id: 'towerarena-1v1',
  tickRate: 30,
  countdownTicks: 90,
  normalTicks: 5_400,
  overtimeTicks: 3_600,
  startingElixirMilli: 6_000,
  maxElixirMilli: 10_000,
  singleElixirPerTickMilli: 12,
  doubleElixirPerTickMilli: 24,
  tripleElixirPerTickMilli: 36,
  normalDoubleElixirStartTick: 3_600,
  overtimeTripleElixirStartTick: 1_800,
  singleCardCooldownTicks: 30,
  doubleCardCooldownTicks: 15,
  tripleCardCooldownTicks: 11,
};

export interface EngineOptions {
  seed: number;
  content: ContentSnapshot;
  arenaId: string;
  decks: Readonly<Record<PlayerId, readonly CardId[]>>;
  rules?: BattleRules;
  diagnosticProfile?: DiagnosticProfile;
  matchId?: string;
  botStrategy?: BotStrategyProvenance;
  matchMetadata?: ReplayMatchMetadata;
}

export function deepFreeze<T>(value: T): T {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    for (const child of Object.values(value as Record<string, unknown>)) deepFreeze(child);
    Object.freeze(value);
  }
  return value;
}

export function cloneContent(content: ContentSnapshot): ContentSnapshot {
  return {
    version: content.version,
    cards: new Map([...content.cards].map(([id, definition]) => [id, deepFreeze(structuredClone(definition))])),
    units: new Map([...content.units].map(([id, definition]) => [id, deepFreeze(structuredClone(definition))])),
    spells: new Map([...content.spells].map(([id, definition]) => [id, deepFreeze(structuredClone(definition))])),
    arenas: new Map([...content.arenas].map(([id, definition]) => [id, deepFreeze(structuredClone(definition))])),
  };
}
